import React from "react";
import PropTypes from "prop-types";
import AtomHed from "components/source/atoms/atom-hed";
import AtomDek from "components/source/atoms/atom-dek";
import RtrImage from "components/source/shared/rtr-image";
import { atomHedAttributesPropType, atomTextAttributesPropType, browserPropType } from "components/propTypes";

const contentItemShape = PropTypes.shape({
  type: PropTypes.oneOf(["text", "image", "group"]).isRequired,
  text: PropTypes.string,
  variant: PropTypes.oneOf(["normal", "bold", "italic"]),
  src: PropTypes.string,
  alt: PropTypes.string,
  layout: PropTypes.oneOf(["horizontal", "vertical"]),
  items: PropTypes.array,
});

const commonShape = PropTypes.shape({
  text: PropTypes.string,
  content: PropTypes.arrayOf(contentItemShape),
  backgroundColor: PropTypes.string,
  modifier: PropTypes.string,
  fontSize: PropTypes.string,
  borderColor: PropTypes.string,
  borderTopColor: PropTypes.string,
  borderRightColor: PropTypes.string,
  borderBottomColor: PropTypes.string,
  borderLeftColor: PropTypes.string,
  href: PropTypes.string,
});

const rowHedObjectArray = PropTypes.arrayOf(commonShape);

const columnObjectArray = PropTypes.arrayOf(
  PropTypes.shape({
    columnListItems: PropTypes.arrayOf(commonShape),
  })
);

const propTypes = {
  backgroundColor: PropTypes.string,
  browser: browserPropType,
  columns: columnObjectArray.isRequired,
  columnsMobile: columnObjectArray.isRequired,
  dekAttributes: atomTextAttributesPropType,
  hedAttributes: atomHedAttributesPropType,
  hedDek: PropTypes.string,
  hedText: PropTypes.string,
  mobileDekAttributes: atomTextAttributesPropType,
  mobileHedAttributes: atomHedAttributesPropType,
  mobileHedDek: PropTypes.string,
  rowHed: rowHedObjectArray.isRequired,
  rowHedMobile: rowHedObjectArray.isRequired,
};

const baseClass = "molecule-comparison-table";

class MoleculeComparisonTable extends React.Component {
  baseClassWith(element) {
    return `${baseClass}${element}`;
  }

  getStyles() {
    return {
      backgroundColor: this.props.backgroundColor,
    };
  }

  getCellStyles(item) {
    const styles = {
      backgroundColor: item?.backgroundColor || "#FFFFFF",
      color: item?.fontColor || "#000000",
      textAlign: item?.modifier || "left",
      fontSize: item?.fontSize || "18",
      borderTop: `1px solid ${item?.borderTopColor || item?.borderColor || "#C6C1BB"}`,
      borderRight: `1px solid ${item?.borderRightColor || item?.borderColor || "#C6C1BB"}`,
      borderBottom: `1px solid ${item?.borderBottomColor || item?.borderColor || "#C6C1BB"}`,
      borderLeft: `1px solid ${item?.borderLeftColor || item?.borderColor || "#C6C1BB"}`,
    };

    return styles;
  }

  getHeaderStyles(item) {
    const styles = {
      backgroundColor: item?.backgroundColor || "#FFFFFF",
      color: item?.fontColor || "#000000",
      textAlign: item?.modifier || "left",
      fontSize: item?.fontSize || "18",
    };

    return styles;
  }

  renderContentItem(item, key) {
    if (item.type === "image") {
      return <RtrImage key={key} src={item.src} alt={item.alt || ""} style={{ maxWidth: "40px", height: "auto" }} />;
    }

    if (item.type === "text") {
      const className =
        item.variant === "bold"
          ? "cell-text cell-text--bold"
          : item.variant === "italic"
          ? "cell-text cell-text--italic"
          : "cell-text";
      return (
        <p key={key} className={className}>
          {item.text}
        </p>
      );
    }

    return null;
  }

  renderCellContent({ content, text }) {
    if (Array.isArray(content)) {
      return content.map((item, idx) => {
        if (item.type === "group") {
          const direction = item.layout === "horizontal" ? "row" : "column";
          return (
            <div key={idx} className={`cell-block cell-block--${direction}`}>
              {item.items.map((subItem, subIdx) => this.renderContentItem(subItem, `${idx}-${subIdx}`))}
            </div>
          );
        }

        return this.renderContentItem(item, idx);
      });
    }

    if (typeof text === "string") {
      return <p className="cell-text">{text}</p>;
    }

    return null;
  }

  headRow() {
    const {
      rowHed,
      rowHedMobile,
      browser: { isMobileViewport },
    } = this.props;

    const rowHead = isMobileViewport ? rowHedMobile : rowHed;
    return (
      <tr>
        <th></th>
        {rowHead.map((cell, i) => (
          <th key={i} className={cell.modifier} style={{ ...this.getHeaderStyles(cell) }}>
            {this.renderCellContent(cell)}
          </th>
        ))}
      </tr>
    );
  }

  contentRows() {
    const {
      columns,
      columnsMobile,
      browser: { isMobileViewport },
    } = this.props;

    const cols = isMobileViewport ? columnsMobile : columns;
    const rows = cols[0].columnListItems;

    return rows.map((row, i) => (
      <tr key={i}>
        {cols.map((col, j) => {
          const cellItem = col.columnListItems[i];
          const cellContent = cellItem.href ? (
            <a className="linkStyle" href={cellItem.href}>
              {this.renderCellContent(cellItem)}
            </a>
          ) : (
            this.renderCellContent(cellItem)
          );

          return (
            <td
              key={j}
              style={{
                ...this.getCellStyles(cellItem),
                cursor: cellItem.href ? "pointer" : "default",
              }}>
              {cellContent}
            </td>
          );
        })}
      </tr>
    ));
  }

  render() {
    const { hedText, mobileHedDek, hedDek, dekAttributes, mobileDekAttributes, hedAttributes, mobileHedAttributes } =
      this.props;

    return (
      <div className={baseClass} style={{ ...this.getStyles(), "--col-count": this.props.columns.length + 1 }}>
        <AtomHed text={hedText} type="h2" hedAttributes={hedAttributes} mobileHedAttributes={mobileHedAttributes} />
        <AtomDek
          text={hedDek}
          mobileText={mobileHedDek}
          dekAttributes={dekAttributes}
          mobileDekAttributes={mobileDekAttributes}
          customClass={this.baseClassWith("__dek")}
        />

        <div className="molecule-comparison-table__table-wrapper">
          <table>
            {this.headRow()}
            {this.contentRows()}
          </table>
        </div>
      </div>
    );
  }
}

MoleculeComparisonTable.propTypes = propTypes;

export default MoleculeComparisonTable;
